/**
 * A reader for `text/event-stream` (the HTML Living Standard's event stream format), for answers
 * streamed over POST: `EventSource` can only GET, so the client reads the body with `fetch` and
 * parses it here. Lines end with LF, CR or CRLF; `:` starts a comment (the server's `: ping`); a blank
 * line dispatches the event. Bytes are decoded as UTF-8 across chunk boundaries, so an Arabic letter
 * split between two network chunks arrives whole.
 */

export interface SseMessage {
  /** The `event:` field, or `message` when the server sent none. */
  event: string
  /** The `data:` lines joined with a line feed. */
  data: string
  /** The `id:` field, when sent. */
  id?: string | undefined
}

/** Incremental parser: feed decoded text with `push`, then `end` once the stream closes. */
export class SseParser {
  private buffer = ''
  /** A CR ended the last chunk: an LF starting the next one belongs to it. */
  private pendingCr = false
  private data: string[] = []
  private event = ''
  private id: string | undefined

  /** The messages completed by this text, in order. */
  push(text: string): SseMessage[] {
    let chunk = text
    if (this.pendingCr && chunk.startsWith('\n')) chunk = chunk.slice(1)
    this.pendingCr = false
    this.buffer += chunk
    const out: SseMessage[] = []
    let start = 0
    for (let i = 0; i < this.buffer.length; i++) {
      const c = this.buffer[i]
      if (c !== '\n' && c !== '\r') continue
      const line = this.buffer.slice(start, i)
      if (c === '\r') {
        if (i + 1 === this.buffer.length) this.pendingCr = true
        else if (this.buffer[i + 1] === '\n') i++
      }
      start = i + 1
      const message = this.line(line)
      if (message) out.push(message)
    }
    this.buffer = this.buffer.slice(start)
    return out
  }

  /** The stream closed: a last event without its blank line is dropped, as the standard says. */
  end(): SseMessage[] {
    this.buffer = ''
    this.data = []
    this.event = ''
    return []
  }

  private line(line: string): SseMessage | null {
    if (line === '') return this.dispatch()
    if (line.startsWith(':')) return null
    const colon = line.indexOf(':')
    const field = colon === -1 ? line : line.slice(0, colon)
    let value = colon === -1 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (field === 'data') this.data.push(value)
    else if (field === 'event') this.event = value
    else if (field === 'id' && !value.includes('\0')) this.id = value
    // `retry` and unknown fields are ignored: the client never reconnects a POST by itself.
    return null
  }

  private dispatch(): SseMessage | null {
    if (this.data.length === 0) {
      this.event = ''
      return null
    }
    const message: SseMessage = { event: this.event || 'message', data: this.data.join('\n') }
    if (this.id !== undefined) message.id = this.id
    this.data = []
    this.event = ''
    return message
  }
}

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError')
}

/**
 * The messages of a response body, as they arrive. Aborting `signal` cancels the body and rejects with
 * an AbortError (a stand-in `fetch`, such as the review harness, may not cancel it by itself).
 */
export async function* readSse(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal | undefined,
): AsyncGenerator<SseMessage> {
  const reader = body.getReader()
  const decoder = new TextDecoder('utf-8')
  const parser = new SseParser()
  const onAbort = () => void reader.cancel().catch(() => undefined)
  if (signal?.aborted) {
    onAbort()
    throw abortError()
  }
  signal?.addEventListener('abort', onAbort, { once: true })
  let finished = false
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (signal?.aborted) throw abortError()
      if (done) {
        finished = true
        yield* parser.push(decoder.decode())
        parser.end()
        return
      }
      yield* parser.push(decoder.decode(value, { stream: true }))
    }
  } finally {
    signal?.removeEventListener('abort', onAbort)
    // The reader stopped early (an error, an abort, or the caller broke off): close the body.
    if (!finished) void reader.cancel().catch(() => undefined)
  }
}
